import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Category } from '../../models/Category'
import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { sortBySeqnum } from '../../utils'
import { ComponentData } from '../../utils/typing'
import { Icon, IconName } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import Label from '../app/design/Label'
import { TableCell } from '../app/design/Table'
import { ArrangeableTable } from '../common/ArrangeableTable'
import { ActionButton } from '../common/ListHeader'
import { ShareLinkModal } from '../common/modals/ShareLinkModal'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { EmptyDashboardLayout } from '../dashboards/EmptyDashboardLayout'
import { SettingsLayout } from './SettingsLayout'

const viewWillAppear = async (base: PocketComponent) => {
    return {
        categories: sortBySeqnum(await RecordFactory.getInstance().loadCategories(base.owner.id!)),
    }
}

function CategoryIcon({ item }: { item: Category }) {
    if (item.image?.url) {
        return <img src={item.image.url} alt={item.name} className="size-9 rounded-full" />
    }
    return (
        <Icon
            icon={`grid.medium`}
            shouldInheritColor
            background="round"
            className="text-primary-700 size-9"
        />
    )
}

function CategoryMoreMenu({ category }: { category: Category }) {
    const user = PSApplication.me()
    const [showShareLinkModal, setShowShareLinkModal] = useState(false)
    const navigate = useNavigate()

    const options = (() => {
        const a = [
            {
                id: 'edit',
                title: `Edit category`,
                icon: 'pencil' as IconName,
            },
            {
                id: 'copy',
                title: `Copy category`,
                icon: 'copy' as IconName,
            },
            {
                id: 'share',
                title: 'Share booking link',
                icon: 'share' as IconName,
            },
        ]
        return a
    })()

    const formattedCategoryNameForSharing = (() => {
        return (category.name ?? '')
            .replaceAll('-', '')
            .replaceAll(' ', '-')
            .replaceAll('&', 'and')
            .toLowerCase()
    })()

    return (
        <>
            <div className="flex w-full justify-end">
                <MoreMenuButton
                    align="end"
                    options={options}
                    onSelectOption={(option) => {
                        switch (option.id) {
                            case 'edit':
                                navigate(`/settings/categories/${category.id}/edit`)
                                break
                            case 'share':
                                setShowShareLinkModal(true)
                                break
                            case 'copy':
                                navigate(`/settings/categories/${category.id}/copy`)
                                break
                            case 'lessons':
                                navigate(`/settings/categories/${category.id}/lessons`)
                                break
                        }
                    }}
                />
            </div>
            <ShareLinkModal
                uri={`/book/${user.username || user.key || ''}/items/${formattedCategoryNameForSharing}`}
                isOpen={showShareLinkModal}
                onClose={() => setShowShareLinkModal(false)}
            />
        </>
    )
}

const render = ({ data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const { logAndDisplayError } = useErrorHandler()
    const [categories, setCategories] = useState(data.categories)

    const onDragEnd = async (source: Category, position: number, list: Category[]) => {
        const originalCategoryList = categories
        setCategories(
            list.map((c, seqnum) => {
                c.seqnum = seqnum + 1
                return c
            })
        )

        const result = await RecordFactory.getInstance().moveCategory(source, position)
        if (!result.ok) {
            logAndDisplayError(result.error)
            setCategories(originalCategoryList)
            return
        }
    }

    return (
        <SettingsLayout
            type="category"
            actionButton={{
                buttonLabel: `Add a category`,
                actions: [
                    {
                        id: 'new',
                        label: `Create a new category`,
                        href: `/settings/categories/new`,
                        icon: 'grid',
                    },
                    {
                        id: 'defaults',
                        label: `Add a category from defaults`,
                        href: `/settings/categories/defaults`,
                        icon: 'documentPen',
                    },
                ],
                icon: 'plus.small',
                intent: 'primary',
            }}
        >
            <ArrangeableTable
                headers={[
                    {
                        label: 'Name',
                    },
                    {
                        label: '',
                    },
                ]}
                droppableId="categories"
                onDragEnd={(s, d, l) => {
                    onDragEnd(
                        s.category,
                        d,
                        l.map((r) => r.category)
                    )
                }}
                rows={categories.map((item) => ({
                    id: item.id ?? '',
                    title: item.name ?? '',
                    category: item,
                    href: `/settings/categories/${item.id}/edit`,
                    cells: [
                        <div className="flex items-center gap-x-4">
                            <CategoryIcon item={item} />
                            <Label size="sm">{item.name}</Label>
                        </div>,
                        <CategoryMoreMenu category={item} />,
                    ],
                }))}
                emptyLayout={
                    <TableCell colSpan={10}>
                        <EmptyDashboardLayout
                            dashboard={
                                new Dashboard({
                                    title: `No categories`,
                                })
                            }
                            searchtext={''}
                            actionButton={
                                <ActionButton
                                    actionButton={{
                                        buttonLabel: `Add a category`,
                                        icon: 'plus.small',
                                        actions: [
                                            {
                                                id: 'new',
                                                label: `Create a new category`,
                                                href: `/settings/categories/new`,
                                            },
                                            {
                                                id: 'defaults',
                                                label: `Add a category from defaults`,
                                                href: `/settings/categories/defaults`,
                                            },
                                        ],
                                        intent: 'secondary',
                                    }}
                                />
                            }
                        />
                    </TableCell>
                }
            />
        </SettingsLayout>
    )
}

export const SetupCategoriesPage = usePSComponent(render, viewWillAppear)
